// THE TempDash brand: one place for the symbol and the wordmark (founder
// 2026-10-02 rebrand). Every app, web page, email and generated icon reads
// from here, so the logo can never drift into four copies again.
//
// ⭐ SWITCHING MARKS. G2 is the mark (the turquoise C1 fallback was removed
// 2026-10-16, no turquoise anywhere). A new mark goes in MARKS; change
// ACTIVE_MARK, then regenerate every PNG:
//   node scripts/brand-assets.mjs
// and rebuild the apps (icons are native). In-app and web drawings follow the
// constant on their own.
//
// The symbol carries the colour and reads on white, on navy and in Gmail's dark
// mode. The wordmark is plain lowercase "tempdash" in Onest Bold: ink on a
// light surface, white on a dark one (Monzo's rule). Where we cannot know the
// background (email), the wordmark takes WORDMARK.email, a mid tone that holds
// on both.
//
// Dependency free (shared/ rule): plain SVG strings. React Native draws them
// with react-native-svg SvgXml, the web inlines them, the asset script rasterises
// them with sharp.

export const ACTIVE_MARK = 'g2'

export const MARKS = {
  // G2 "Two dashes": two leaning rounded pills stacked. Shifts, and speed.
  g2: {
    name: 'Two dashes',
    viewBox: '0 0 100 100',
    body:
      '<defs>' +
      '<linearGradient id="tdG2a" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#3EE6E8"/><stop offset="1" stop-color="#1C7A9C"/></linearGradient>' +
      '<linearGradient id="tdG2b" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#A9A3FF"/><stop offset="1" stop-color="#218FB7"/></linearGradient>' +
      '</defs>' +
      '<g transform="skewX(-14) translate(12 0)">' +
      '<rect x="8" y="10" width="74" height="42" rx="21" fill="url(#tdG2a)"/>' +
      '<rect x="16" y="46" width="74" height="42" rx="21" fill="url(#tdG2b)" fill-opacity="0.88"/>' +
      '</g>',
    // One flat colour (notification + monochrome icons): the overlap would melt
    // the two pills into one blob, so they part with a clear gap instead.
    mono:
      '<g transform="skewX(-14) translate(12 0)">' +
      '<rect x="8" y="8" width="74" height="38" rx="19"/>' +
      '<rect x="16" y="54" width="74" height="38" rx="19"/>' +
      '</g>',
  },
}

// The full SVG for a mark. `fill` paints it one flat colour (a white silhouette
// for Android notification and monochrome icons), using the mark's `mono` cut
// when it has one.
export function markSvg({ mark = ACTIVE_MARK, size = 100, fill = null } = {}) {
  const m = MARKS[mark] || MARKS[ACTIVE_MARK]
  let body = m.body
  if (fill) {
    body = m.mono
      ? `<g fill="${fill}">${m.mono}</g>`
      : body.replace(/fill="[^"]*"/g, `fill="${fill}"`).replace(/ fill-opacity="[^"]*"/g, '')
  }
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="${m.viewBox}">${body}</svg>`
}

// The brand's own colours OUTSIDE the mark (founder 2026-10-15: no turquoise,
// the brand is G2). palette.brand in both apps and every brand moment (story
// rings and stickers, the My Work hero, the TempDash wallet card) read these.
//   solid    the G2 teal (the top pill's dark stop). White on it is 4.9:1.
//   gradient teal to blue, the two pills' dark stops: a brand fill or wash.
//   ring     teal to lavender, the two pills' far ends: a story ring.
//   onDark   lavender, the bright stop: a brand glyph or word on a dark ground.
//   ink      the wordmark ink, the deep end of a brand card.
export const BRAND_COLOURS = {
  solid: '#1C7A9C',
  gradient: ['#1C7A9C', '#218FB7'],
  ring: ['#1C7A9C', '#A9A3FF'],
  onDark: '#A9A3FF',
  ink: '#14213D',
}

export const WORDMARK = {
  text: 'tempdash',
  font: 'Onest',
  weight: '700',
  // Tracking as a fraction of the font size (-0.8px at 24px).
  tracking: -0.033,
  ink: '#14213D',     // on light surfaces
  onDark: '#FFFFFF',  // on dark surfaces
  email: '#1C7A9C',   // background unknown: 4.9:1 on white, 3.3:1 on Gmail's dark
  // The symbol sits this many font sizes wide beside the word, this far apart.
  markScale: 1.45,
  gap: 0.35,
}

// The G2 mark as its TWO PILLS, each on its own 100 x 100 canvas in the same
// place it sits in the whole mark, so the launch splash (BootSplash in each app)
// can move them apart and bring them back to exactly the logo. Same gradients
// and skew as MARKS.g2; each pill keeps its own gradient ids so two can be on
// screen at once.
export const MARK_PILLS = {
  top:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<defs><linearGradient id="tdPillA" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#3EE6E8"/><stop offset="1" stop-color="#1C7A9C"/></linearGradient></defs>' +
    '<g transform="skewX(-14) translate(12 0)"><rect x="8" y="10" width="74" height="42" rx="21" fill="url(#tdPillA)"/></g>' +
    '</svg>',
  bottom:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<defs><linearGradient id="tdPillB" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#A9A3FF"/><stop offset="1" stop-color="#218FB7"/></linearGradient></defs>' +
    '<g transform="skewX(-14) translate(12 0)"><rect x="16" y="46" width="74" height="42" rx="21" fill="url(#tdPillB)" fill-opacity="0.88"/></g>' +
    '</svg>',
  // The bottom pill fully opaque, for the splash's orbit: in motion the near pill
  // must hide the far one, not show it through its 0.88 (that read as the two
  // melting into each other). Faded out as they settle, so the rest frame is the
  // logo's own blend.
  bottomSolid:
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' +
    '<defs><linearGradient id="tdPillBS" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#A9A3FF"/><stop offset="1" stop-color="#218FB7"/></linearGradient></defs>' +
    '<g transform="skewX(-14) translate(12 0)"><rect x="16" y="46" width="74" height="42" rx="21" fill="url(#tdPillBS)"/></g>' +
    '</svg>',
}
